import {
  ArrowLeftLg,
  ArrowRightLg,
  Button,
  mediaHelper,
  paletteHelper,
  radiusHelper,
  spacingHelper,
} from '@property.com.au/ui-kit';
import { FunctionComponent } from 'react';
import { styled } from 'styled-components';
import { useMediaGalleryCarousel } from '../../../../contexts/MediaGalleryCarouselContext/MediaGalleryCarouselContext';
import {
  MediaGalleryZoomOutIcon,
  MediaGalleryZoomInIcon,
} from '../MediaGalleryZoomIcons/MediaGalleryZoomIcons';
import { MediaGalleryTimelineData } from '../MediaGalleryTimeline/MediaGalleryTimeline';
import { MediaCount, MediaCountButtonsV2 } from '../../../MediaCountButtonsV2/MediaCountButtonsV2';
import { SwiperClass } from 'swiper/react';

import { MediaItemV2 } from '../../../../../app-kit/components/Image/types';
import { MediaGalleryImageSearchButton } from '../MediaGalleryImageSeachButton/MediaGalleryImageSeachButton';
import { MediaGalleryActionBar } from '../MediaGalleryActionBar/MediaGalleryActionBar';

const RoundButton = styled(Button)`
  background: ${paletteHelper('backgroundPrimary')};
  position: absolute;
  top: calc(50% - 40px); /* Half button height */
  z-index: 10;
  pointer-events: auto;

  && {
    color: ${paletteHelper('textPrimary')};
  }

  &:focus,
  &:focus-visible,
  &:focus-within,
  &:hover {
    background-color: ${paletteHelper('backgroundHoveredPrimary')};
  }

  &:focus-visible::after {
    border-radius: ${radiusHelper('full')};
  }

  &:active {
    background-color: ${paletteHelper('backgroundPressedPrimary')};
  }
`;

const PreviousArrowButton = styled(RoundButton)`
  left: ${spacingHelper('medium')};
`;

const NextArrowButton = styled(RoundButton)`
  right: ${spacingHelper('medium')};
`;

const ZoomButton = styled(RoundButton)`
  right: ${spacingHelper('medium')};
  bottom: 80px;
  top: unset;

  svg {
    width: 16px;
  }
`;

const Navigation = styled.div`
  position: absolute;
  padding: 0 ${spacingHelper('large')};
  width: 100%;
  top: 0;
  height: 100%;
  z-index: 10;
  pointer-events: none;
  will-change: transform;
`;

const MediaCountButtonsContainer = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: ${spacingHelper('small')};
  display: flex;
  gap: ${spacingHelper('extraSmall')};
  pointer-events: auto;

  ${mediaHelper('mobile')} and (orientation: portrait) {
    left: ${spacingHelper('small')};
    transform: none;
  }
`;

const onMediaCountClick = (imageIndexFrom: number, swiper: SwiperClass) => {
  swiper.slideTo(imageIndexFrom);
};

interface MediaGalleryControlsProps {
  mediaButtonCounts: MediaCount[];
  timeline: MediaGalleryTimelineData[];
  galleryItems: Array<MediaItemV2>;
}

export const MediaGalleryControls: FunctionComponent<MediaGalleryControlsProps> = ({
  mediaButtonCounts,
  timeline,
  galleryItems,
}) => {
  const { swiper, isBeginning, isEnd, isZoomed, isTouchScreen, isUIHidden } =
    useMediaGalleryCarousel();

  if (!swiper || isUIHidden) return null;

  return (
    <Navigation role="group" aria-label="Carousel controls">
      <MediaCountButtonsContainer>
        <MediaCountButtonsV2
          counts={mediaButtonCounts}
          onClickHandler={(imageIndexFrom: number) => onMediaCountClick(imageIndexFrom, swiper)}
        />
      </MediaCountButtonsContainer>
      {!isBeginning && !isTouchScreen && (
        <PreviousArrowButton
          hideLabel
          compact
          sizeVariant="medium"
          icon={<ArrowLeftLg />}
          onClick={() => swiper.slidePrev()}
        >
          Previous slide
        </PreviousArrowButton>
      )}
      {!isEnd && !isTouchScreen && (
        <NextArrowButton
          hideLabel
          compact
          icon={<ArrowRightLg />}
          sizeVariant="medium"
          onClick={() => swiper.slideNext()}
        >
          Next slide
        </NextArrowButton>
      )}
      {!isTouchScreen && (
        <ZoomButton
          hideLabel
          compact
          icon={isZoomed ? <MediaGalleryZoomOutIcon /> : <MediaGalleryZoomInIcon />}
          onClick={() => (isZoomed ? swiper.zoom.out() : swiper.zoom.in())}
          sizeVariant="medium"
        >
          {isZoomed ? 'Zoom Out' : 'Zoom In'}
        </ZoomButton>
      )}

      <MediaGalleryActionBar timeline={timeline} galleryItems={galleryItems} />
      <MediaGalleryImageSearchButton galleryItems={galleryItems} />
    </Navigation>
  );
};
